import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default class Utils {
  /**
   * Get item from localStorage
   * @param {string} key - The key to identify the item in localStorage
   * @returns {any|null} Parsed JSON data from localStorage, or null if the key does not exist
   */
  static localStorageGetItem = (key) => {
    const data = localStorage.getItem(key);
    if (data) return JSON.parse(data);
    return null;
  };

  /**
   * Set item in localStorage
   * @param {string} key - The key to identify the item in localStorage
   * @param {any} data - Data to be stored in localStorage (will be JSON.stringify-ed)
   */
  static localStorageSetItem = (key, data) => {
    localStorage.setItem(key, JSON.stringify(data));
  };

  /**
   * Remove item from localStorage
   * @param {string} key - The key to identify the item in localStorage
   */
  static localStorageRemoveItem = (key) => {
    localStorage.removeItem(key);
  };

  /**
   * This function is made to handle success and error callback!
   * @param {Promise} promise - The promise to be handled
   * @returns {Promise<Array>} A promise that resolves to an array with either [null, data] on success or [err] on failure
   */
  static async parseResponse(promise) {
    return promise.then((data) => [null, data]).catch((err) => [err]);
  }

  static generateUrl(url, params) {
    let i = 0,
      key;
    for (key in params) {
      if (i === 0) {
        url += "?";
      } else {
        url += "&";
      }
      url += key;
      url += "=";
      url += params[key];
      i++;
    }
    return url;
  }

  static shortenString(string, maxLength) {
    const charsToKeep = Math.floor((maxLength - 3) / 2);
    return string.length > maxLength
      ? string.substring(0, charsToKeep) +
          "..." +
          string.substring(string.length - charsToKeep)
      : string;
  }

  static successToastMessage(message) {
    toast.dismiss();
    toast.success(message, {
      position: "top-center",
      autoClose: 3000,
      toastId: "successToast"
    });
  }
  static failureToastMessage(message) {
    toast.dismiss();
    toast.error(message, {
      position: "top-center",
      autoClose: 3000,
      toastId: "failureToast"
    });
  }
  static capitalizeFirstLetter(string) {
    return string?.charAt(0)?.toUpperCase() + string?.slice(1)?.toLowerCase();
  }
  static addDays(date, days) {
    return new Date(date.getTime() + days * 24 * 60 * 60 * 1000);
  }
  static formatTimestampNormal = (timestamp) => {
    const date = new Date(timestamp * 1000);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();

    return `${month}/${day}/${year}`;
  };

  // eslint-disable-next-line no-unused-vars
  static epochTimeToLocalDateString = (timestamp) => {
    if (!timestamp) return "N/A";

    // Convert to number in case it's passed as a string

    const timestampNumber = Number(timestamp);

    // Check if the timestamp is in seconds (10 digits) or milliseconds (13 digits)

    const isSeconds = timestampNumber.toString().length === 10;

    // If it's in seconds, convert it to milliseconds

    const date = new Date(isSeconds ? timestampNumber * 1000 : timestampNumber);

    return date.toLocaleDateString(); // Customize format as needed
  };

  static convertPlatformName = (platformName) => {
    switch (platformName) {
      case "Facebook":
        return "Facebook Ad";
      case "Instagram":
        return "Instagram Ad";
      case "Google":
        return "Google Ad";
      case "Bing":
        return "Bing Ad";
      case "LinkedIn":
        return "LinkedIn Ad";
      default:
        return "Ad";
    }
  };
  static isValidWebsiteUrl(url) {
    // Regular expression for URLs with or without www.
    const urlPattern =
      // eslint-disable-next-line no-useless-escape
      /^(http:\/\/|https:\/\/)(www\.)?([a-zA-Z0-9-]+\.[a-zA-Z]{2,})(\.[a-zA-Z]{2,})?([\/\?#].*)?$/;

    // Test the input URL
    const isValidFormat = urlPattern.test(url);

    if (!isValidFormat) {
      return false;
    }

    // Further validation based on the number of dots and presence of 'www'
    const dotCount = (url.match(/\./g) || []).length;

    if (url.includes("www.")) {
      // If 'www' is present, there must be at least 2 dots
      return dotCount >= 2;
    } else {
      // If 'www' is not present, there must be at least 1 dot
      return dotCount >= 1;
    }
  }

  // Resolve a Stripe priceId -> [planName, planTime, planPrice].
  // Prefer live plan data (managed in the admin dashboard, fetched via getPlans)
  // when provided, since plan prices/IDs can change there. Falls back to the
  // legacy env-var mapping when no live plans are passed or no match is found.
  static getSubscriptionPlanInfo = (priceID, plans = []) => {
    if (!priceID) return ["No Plan purchased", "", ""];

    if (Array.isArray(plans) && plans.length) {
      for (const p of plans) {
        if (p?.monthly?.priceId && p.monthly.priceId === priceID) {
          return [p.name, "month", String(p.monthly.amount ?? "")];
        }
        if (p?.yearly?.priceId && p.yearly.priceId === priceID) {
          return [p.name, "year", String(p.yearly.amount ?? "")];
        }
      }
    }

    switch (priceID) {
      case process.env.REACT_APP_FREELANCE_MONTHLY_PLAN_PRICE_ID:
        // Here array indicates --> [planName, planTime, planPrice]
        return ["Freelance", "month", "12"];
      case process.env.REACT_APP_BUSINESS_MONTHLY_PLAN_PRICE_ID:
        return ["Business", "month", "24"];
      case process.env.REACT_APP_ENTERPRISE_MONTHLY_PLAN_PRICE_ID:
        return ["Enterprise", "month", "50"];
      case process.env.REACT_APP_FREELANCE_ANNUAL_PLAN_PRICE_ID:
        return ["Freelance", "year", "100"];
      case process.env.REACT_APP_BUSINESS_ANNUAL_PLAN_PRICE_ID:
        return ["Business", "year", "200"];
      case process.env.REACT_APP_ENTERPRISE_ANNUAL_PLAN_PRICE_ID:
        return ["Enterprise", "year", "400"];
      default:
        return ["No Plan purchased", "", ""];
    }
  };
  static formatDateInStringForm(epochTime) {
    const date = new Date(epochTime);
    const options = { year: "numeric", month: "long", day: "2-digit" };
    return date.toLocaleDateString("en-US", options);
  }
  static formatTimeHavingTimeDateDay(epochTime) {
    const date = new Date(epochTime);

    const now = new Date();
    const yesterday = new Date();
    yesterday.setDate(now.getDate() - 1);

    const isToday = date.toDateString() === now.toDateString();
    const isYesterday = date.toDateString() === yesterday.toDateString();

    const options = { hour: "2-digit", minute: "2-digit", hour12: true };
    const time = date.toLocaleTimeString("en-US", options);

    if (isToday) {
      return `Today - ${time}`;
    } else if (isYesterday) {
      return `Yesterday - ${time}`;
    } else {
      const dateOptions = { year: "numeric", month: "long", day: "2-digit" };
      const formattedDate = date.toLocaleDateString("en-US", dateOptions);
      return `${formattedDate} - ${time}`;
    }
  }

  static manageTimer = (
    resendStart,
    seconds,
    minutes,
    setSeconds,
    setMinutes,
    setResendStart,
    intervalId
  ) => {
    if (resendStart && seconds > 0) {
      setSeconds(seconds - 1);
    }
    if (seconds === 0) {
      if (minutes === 0) {
        clearInterval(intervalId);
        setResendStart(false); // Stop the resend process when the timer reaches zero
      } else {
        setSeconds(59);
        setMinutes(minutes - 1);
      }
    }
  };
  static fixUrl(url) {
    if (!url) return url;

    return url
      .replace(/\s/g, "%20")
      .replace(/\(/g, "%28")
      .replace(/\)/g, "%29");
  }

  /**
   * Convert relative URLs to absolute URLs for cross-origin access
   * @param {string} url - The URL to convert
   * @returns {string} Absolute URL
   */
  static makeAbsoluteUrl(url) {
    if (!url) return url;

    // If already absolute URL, return as is
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }

    // If starts with //, add protocol
    if (url.startsWith("//")) {
      return `https:${url}`;
    }

    // If relative path, make it absolute using current origin
    if (url.startsWith("/")) {
      return `${window.location.origin}${url}`;
    }

    // If no protocol or path, assume it's a relative path
    return `${window.location.origin}/${url}`;
  }

  /**
   * Prepare image URL for cross-origin usage (template editor)
   * @param {string} url - The image URL to prepare
   * @returns {string} Fixed and absolute URL
   */
  static prepareImageUrlForEditor(url) {
    if (!url) return url;

    // First make it absolute
    const absoluteUrl = this.makeAbsoluteUrl(url);

    // Handle CloudFront URLs - they need special CORS handling
    if (absoluteUrl.includes("cloudfront.net")) {
      console.log(
        "☁️ CloudFront image detected, using proxy approach:",
        absoluteUrl
      );
      // For CloudFront images, we'll use a different strategy
      // Return the original URL but with additional CORS handling
      return this.fixUrl(absoluteUrl);
    }

    // Then fix any URL encoding issues
    return this.fixUrl(absoluteUrl);
  }

  /**
   * Create a CORS-friendly proxy URL for problematic images
   * @param {string} url - The original image URL
   * @returns {string} Proxy URL that can handle CORS
   */
  static createProxyImageUrl(url) {
    if (!url) return url;

    // For CloudFront images, we can try using a CORS proxy
    if (url.includes("cloudfront.net")) {
      // Option 1: Use the template editor as a proxy
      const templateEditorUrl = process.env.REACT_APP_TEMPLATE_EDITOR_URL;
      if (templateEditorUrl) {
        return `${templateEditorUrl}/api/image-proxy?url=${encodeURIComponent(url)}`;
      }

      // Option 2: Use a different approach - return original for now
      return url;
    }

    return url;
  }

  /**
   * Convert image URL to data URL for cross-origin use
   * @param {string} url - The image URL to convert
   * @returns {Promise<string>} Promise that resolves to data URL
   */
  static async convertImageToDataUrl(url) {
    if (!url) return url;

    return new Promise((resolve) => {
      // Try to load the image on the current domain first
      const img = new Image();

      // Don't use crossOrigin for CloudFront images from same domain
      if (
        !url.includes("cloudfront.net") ||
        url.startsWith(window.location.origin)
      ) {
        img.crossOrigin = "anonymous";
      }

      img.onload = function () {
        try {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          canvas.width = this.naturalWidth;
          canvas.height = this.naturalHeight;

          ctx.drawImage(this, 0, 0);
          const dataUrl = canvas.toDataURL("image/jpeg", 0.8);

          console.log(
            "✅ Successfully converted image to data URL:",
            url.substring(0, 50) + "..."
          );
          resolve(dataUrl);
        } catch (error) {
          console.error("❌ Failed to convert image to data URL:", error);
          resolve(url); // Fallback to original URL
        }
      };

      img.onerror = function (error) {
        console.error("❌ Failed to load image for conversion:", url, error);
        resolve(url); // Fallback to original URL
      };

      img.src = url;
    });
  }

  /**
   * Test if an image URL is accessible for cross-origin loading
   * @param {string} url - The image URL to test
   * @returns {Promise<boolean>} Promise that resolves to true if accessible
   */
  static async testImageAccessibility(url) {
    if (!url) return false;

    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous"; // Request CORS

      img.onload = () => {
        console.log("✅ Image loaded successfully:", url);
        resolve(true);
      };

      img.onerror = (error) => {
        console.error("❌ Image failed to load:", url, error);
        resolve(false);
      };

      // Set timeout to avoid hanging
      setTimeout(() => {
        console.warn("⏰ Image loading timeout:", url);
        resolve(false);
      }, 10000);

      img.src = url;
    });
  }
}
